import React, { useMemo, useCallback, useEffect } from 'react';
import { IItemForGetShortInfoWithStatus, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	setIDsRestaurant: (value: string[]) => void;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	IDsSelectedRestaurant: string[];
	customerId: string;
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	setFetchedRestaurants,
	setIDsRestaurant,
	fetchedRestaurants,
	IDsSelectedRestaurant,
	customerId,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		customerId && !fetchedRestaurants.length ? getRestaurantsShortInfo : null,
		{
			customerId: customerId || '',
		},
	);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
			setIDsRestaurant(updatedSelectedItems);
		},
		[IDsSelectedRestaurant, setIDsRestaurant],
	);

	const restaurantsList = useMemo(() => {
		return IDsSelectedRestaurant.map((el) => {
			const label = fetchedRestaurants.find((item) => item.id === el)?.name || '';

			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [IDsSelectedRestaurant, fetchedRestaurants, handleRemove]);

	useEffect(() => {
		if (response) {
			setFetchedRestaurants(response.data.restaurants);
		}
	}, [response]);

	return (
		<div className={style.container} data-test="RestaurantsMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={fetchedRestaurants}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					setIDsRestaurant={setIDsRestaurant}
					customerId={customerId}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
